import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { sqItemFinderStore } from '@/core/core.stores';
import { updateSearch } from '@/itemFinder/itemFinder.actions';
import { SearchTypeEnum } from '@/itemFinder/itemFinder.constants';
import { getMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { doTrack } from '@/track/trackProxy.service';
import { base64guid } from '@/utilities/guid.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqStoredTablesApi } from '@seeqdev/api/endpoint/StoredTablesApi';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import { SearchTypeEnum as ScalingTableSearchTypeEnum } from '@seeqdev/api/model/ScalingTableUUIDColumnSearchV1';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type {
  AIPayload,
  AncestorPredicate,
  FixedListSearch,
  HierarchyLevelColumn,
  HierarchySearch,
  HierarchySearchOptions,
  Property,
  ScalingTableSearch,
  ScalingTableSearchOptions,
  Search,
  SearchType,
  StoredTableSearch,
  Value,
} from '@/itemFinder/itemFinder.constants';
import type { DatasourceLabel, HierarchyPath, PropertiesList } from '@/itemFinder/itemFinder.store';
import type { Datasource, SelectedSearchItem } from '@/search/search.types';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TrackInformation } from '@/track/track.types';
import type { SelectOptionIF } from '@/usage/Usage.page';
import type { AncestorFilterInputV1 } from '@seeqdev/api/model/AncestorFilterInputV1';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { FixedListFilterInputV1 } from '@seeqdev/api/model/FixedListFilterInputV1';
import type { FixedListSearchV1 } from '@seeqdev/api/model/FixedListSearchV1';
import type { HierarchySearchOutputV1 } from '@seeqdev/api/model/HierarchySearchOutputV1';
import type { HierarchySearchV1 } from '@seeqdev/api/model/HierarchySearchV1';
import type { ItemFinderInputV1 } from '@seeqdev/api/model/ItemFinderInputV1';
import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { PropertiesForItemUUIDColumn } from '@seeqdev/api/model/PropertiesForItemUUIDColumn';
import type { PropertyFilterInputV1 } from '@seeqdev/api/model/PropertyFilterInputV1';
import type { PropertyInputV1 } from '@seeqdev/api/model/PropertyInputV1';
import type { PropertySearchV1 } from '@seeqdev/api/model/PropertySearchV1';
import type { ScalingTableUUIDColumnSearchV1 } from '@seeqdev/api/model/ScalingTableUUIDColumnSearchV1';
import type { StoredTableSearchV1 } from '@seeqdev/api/model/StoredTableSearchV1';
import type * as models from '@seeqdev/api/model/models';

/** Sentinel for globally scoped (null scopedTo) in WITHIN predicates. */
export const SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE = '__NULL__';

export const createDefaultStoredTableNameProperty = (searchId: string): Property => {
  const propertyId = base64guid();
  const valueId = base64guid();
  return {
    propertyId,
    searchId,
    propertyName: SeeqNames.Properties.Name,
    operator: OperatorEnum.EQUALS,
    values: [{ value: '', propertyId, searchId, valueId }],
    isColumnSelect: true,
  };
};

type StoredTableSimpleSearchCandidate = {
  predicates?: Property[];
  ancestorPredicates?: unknown[];
  ancestorId?: string;
};

/** Predicates that represent user-configured filters (excludes Scoped To and empty placeholders). */
export const getStoredTableUserPredicates = (predicates: Property[] | undefined): Property[] =>
  (predicates ?? []).filter(
    (property) => property.propertyName && property.propertyName !== SeeqNames.Properties.ScopedTo,
  );

/** Infers simple mode from search shape (single Name column). */
export const isStoredTableSimpleSearch = (search: StoredTableSimpleSearchCandidate): boolean => {
  const userPredicates = getStoredTableUserPredicates(search.predicates);

  if (userPredicates.length !== 1 || search.ancestorPredicates?.length || search.ancestorId) {
    return false;
  }

  const predicate = userPredicates[0];
  return (
    predicate.propertyName === SeeqNames.Properties.Name &&
    predicate.operator === OperatorEnum.EQUALS &&
    !!predicate.columnId
  );
};

export const findScopedToProperty = (predicates: Property[] | undefined): Property | undefined =>
  predicates?.find((property) => property.propertyName === SeeqNames.Properties.ScopedTo);

export const isScopedToLimitedToCurrentWorkbook = (
  scopedToProperty: Property | undefined,
  workbookId: string | undefined,
): boolean =>
  !!scopedToProperty &&
  !!workbookId &&
  scopedToProperty.operator === OperatorEnum.EQUALS &&
  scopedToProperty.values.length === 1 &&
  scopedToProperty.values[0]?.value === workbookId;

export const buildScopedToPropertyValues = (
  limitToCurrentWorkbook: boolean,
  workbookId: string,
  propertyId: string,
  searchId: string | number,
): Value[] => {
  if (limitToCurrentWorkbook) {
    return [
      {
        value: workbookId,
        propertyId,
        searchId: searchId as string,
        valueId: base64guid(),
      },
    ];
  }

  return [
    {
      value: SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE,
      propertyId,
      searchId: searchId as string,
      valueId: base64guid(),
    },
    {
      value: workbookId,
      propertyId,
      searchId: searchId as string,
      valueId: base64guid(),
    },
  ];
};

export const searchSupportsScopedToPredicate = (search: Search): boolean =>
  search.searchType === SearchTypeEnum.PROPERTY || search.searchType === SearchTypeEnum.STOREDTABLE;

/** Whether the limit-to-current-workbook UI toggle is shown for this search. */
export const searchShowsScopedToLimitToggle = (search: Search, tableScopedTo: string | undefined): boolean => {
  // A Global Table Definition (undefined tableScopedTo) has no Analysis to limit the search to
  if (!tableScopedTo) {
    return false;
  }
  if (search.searchType === SearchTypeEnum.PROPERTY) {
    return true;
  }
  if (search.searchType === SearchTypeEnum.STOREDTABLE) {
    return !(search as StoredTableSearch).isSimpleSearch;
  }
  return false;
};

export const ensureSearchScopedTo = (search: Search, workbookId: string | undefined): void => {
  if (!workbookId || !searchSupportsScopedToPredicate(search)) {
    return;
  }

  if (findScopedToProperty(search.predicates)) {
    return;
  }

  updateSearch({
    searchId: search.searchId,
    action: 'ADD_PROPERTY',
    propertyData: {
      propertyName: SeeqNames.Properties.ScopedTo,
      operator: OperatorEnum.WITHIN,
    },
    propertyValues: [SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE, workbookId],
  });
};

export const formatPropertyFilterValues = (
  propertyName: string,
  operator: string,
  localValues: { value: string | number }[],
): { value: string | number | null }[] =>
  localValues.map((v) => {
    if (
      propertyName === SeeqNames.Properties.ScopedTo &&
      operator === OperatorEnum.WITHIN &&
      (v.value === SCOPED_TO_GLOBALLY_SCOPED_NULL_VALUE || v.value === '')
    ) {
      return { value: null };
    }
    if (!v.value && propertyName === SeeqNames.Properties.ScopedTo) {
      // If executing an existing item finder with a workbench search without changing the value, use the existing value
      return { value: localValues[0]?.value ?? null };
    }
    return { value: v.value };
  });

// API payload formatting

/**
 * Filters out hidden exclusion predicates from a property list, so that they can be replaced by
 * the canonical hidden exclusion predicates passed explicitly. This prevents duplicates when the
 * hiddenExclusionPredicates are spread back in.
 */
export const filterHiddenExclusions = (properties: any[], hiddenExclusions: PropertyFilterInputV1[]) =>
  properties.filter(
    (property) =>
      !hiddenExclusions.some(
        (exclusion) =>
          property.propertyName === exclusion.propertyName &&
          property.operator === exclusion.operator &&
          property.values?.[0]?.value === exclusion.values?.[0]?.value,
      ),
  );

export function formatFixedListSearches(searches: FixedListSearch[]): FixedListSearchV1[] | undefined {
  return searches.map((search) => {
    const itemIds = search.itemIds.map((item: any) => (item.id ? item.id : item));

    return {
      searchType: search.searchType,
      isInclude: search.isInclude,
      itemIds,
      searchOrder: search.searchOrder,
    };
  });
}

export function formatHierarchySearches(
  searches: HierarchySearch[],
  hiddenExclusionPredicates: PropertyFilterInputV1[],
): HierarchySearchV1[] | undefined {
  return searches.map((search) => {
    const localPredicates = (search.predicates ?? []).filter((property) => !!property.propertyName);
    const userPredicates = filterHiddenExclusions(localPredicates, hiddenExclusionPredicates);

    return {
      searchType: search.searchType,
      isInclude: search.isInclude,
      hierarchyId: search.hierarchyId,
      searchOrder: search.searchOrder,
      predicates: [...(formatProperties(userPredicates) ?? []), ...hiddenExclusionPredicates],
      ancestorPredicates: formatAncestorPredicates(search.ancestorPredicates ?? []),
      level: search.level,
      fixedListPredicates: formatHierarchyFixedListSearches(search.hierarchyPaths ?? []),
    };
  });
}

export function formatStoredTableSearches(
  searches: StoredTableSearch[],
  hiddenExclusionPredicates: PropertyFilterInputV1[],
): StoredTableSearchV1[] {
  return searches.map((search) => {
    const localPredicates = (search.predicates ?? []).filter((property) => property.propertyName !== '');
    const userPredicates = filterHiddenExclusions(localPredicates, hiddenExclusionPredicates);

    return {
      searchType: SearchTypeEnum.STOREDTABLE,
      isInclude: search.isInclude,
      storedTableId: search.storedTableId,
      ancestorId: search.ancestorId,
      predicates: [...(formatTableProperties(userPredicates) ?? []), ...hiddenExclusionPredicates],
      types: search.types,
      ancestorPredicates: [...formatAncestorPredicates(search.ancestorPredicates ?? [])],
      searchOrder: search.searchOrder,
    };
  });
}

export function formatValues(values: { value: string | number | null }[]): { value: string | number | null }[] {
  return values.map((value) => ({
    value: value.value,
  }));
}

export function formatProperties(properties: any): any[] | undefined {
  return properties.map(
    (property: {
      operator: string;
      propertyName: { value: string };
      propertyId: string;
      values: [{ value: string | number }];
    }) => {
      const propertyName = property.propertyName.value ?? property.propertyName;
      const localValues = property.values ?? [];
      return {
        operator: property.operator,
        propertyName,
        values: formatValues(formatPropertyFilterValues(propertyName, property.operator, localValues)),
      };
    },
  );
}

export function formatTableProperties(properties: any): any[] | undefined {
  return properties.map(
    (property: {
      operator: string;
      propertyName: { value: string };
      propertyId: string;
      values: [{ value: string | number }];
      columnId: string;
      isColumnSelect: boolean;
    }) => {
      const propertyName = property.propertyName.value ?? property.propertyName;
      const localValues = property.values ?? [];
      return {
        operator: property.operator,
        propertyName,
        values: property.isColumnSelect
          ? []
          : formatValues(formatPropertyFilterValues(propertyName, property.operator, localValues)),
        columnId: property.isColumnSelect ? property.columnId : undefined,
      };
    },
  );
}

export function formatAncestorPredicates(ancestorPredicates: AncestorPredicate[]): AncestorFilterInputV1[] {
  return ancestorPredicates
    .filter((ancestorPredicate) => (ancestorPredicate.predicates ?? []).some((property) => !!property.propertyName))
    .map((ancestorPredicate) => {
      const localPredicates = (ancestorPredicate.predicates ?? []).filter((property) => !!property.propertyName);

      return {
        predicates: formatTableProperties(localPredicates) ?? [],
        level: ancestorPredicate.level,
      };
    });
}

export function formatHierarchyFixedListSearches(hierarchyPaths: HierarchyPath[]): FixedListFilterInputV1[] {
  return hierarchyPaths.map((hierarchyPath) => ({
    nodePaths: hierarchyPath.nodes.map((node) => ({
      columnId: node.columnId,
      value: { value: node.value },
    })),
  }));
}

export function formatPropertySearches(
  searches: any,
  options: {
    types: string[] | undefined;
    hiddenExclusionPredicates: PropertyFilterInputV1[];
  },
): PropertySearchV1[] | undefined {
  return searches.map(
    (search: {
      searchType: string;
      isInclude: boolean;
      folderId: { id: string };
      ancestorId: { id: string };
      types: any;
      searchId: string;
      predicates: any;
      ancestorPredicates?: AncestorPredicate[];
      searchOrder?: number;
    }) => {
      // Filter out invalid predicates. A predicate is valid only when propertyName is truthy.
      // When the Search is initialized, we add an empty predicate to show the predicate form in the UI,
      // but these empty predicates (with undefined/empty propertyName) should not be sent to the backend.
      const localPredicates = (search.predicates ?? []).filter((property: PropertiesList) => property.propertyName);
      const userPredicates = filterHiddenExclusions(localPredicates, options.hiddenExclusionPredicates);

      return {
        searchType: SearchTypeEnum.PROPERTY, // Always property search, even if the search option is folder
        isInclude: search.isInclude,
        folderId: search.folderId?.id ?? search.folderId,
        ancestorId: search.ancestorId?.id ?? search.ancestorId,
        predicates: [...(formatProperties(userPredicates) ?? []), ...options.hiddenExclusionPredicates],
        types: options.types ?? search.types,
        ancestorPredicates: formatAncestorPredicates(search.ancestorPredicates ?? []),
        searchOrder: search.searchOrder,
      };
    },
  );
}

const buildItemFinderTrackInformation = (itemFinder: ItemFinderInputV1): TrackInformation => {
  const trackInformation: TrackInformation = {
    scopedTo: itemFinder.scopedTo,
    name: itemFinder.name,
    searchType: getItemFinderSearchType(itemFinder),
  };

  if (itemFinder.propertySearches?.length)
    trackInformation.propertySearches = JSON.stringify(itemFinder.propertySearches);
  if (itemFinder.fixedListSearches?.length)
    trackInformation.fixedListSearches = JSON.stringify(itemFinder.fixedListSearches);
  if (itemFinder.hierarchySearches?.length)
    trackInformation.hierarchySearches = JSON.stringify(itemFinder.hierarchySearches);
  if (itemFinder.storedTableSearches?.length)
    trackInformation.storedTableSearches = JSON.stringify(itemFinder.storedTableSearches);
  if (itemFinder.scalingTableUUIDColumnSearches?.length)
    trackInformation.scalingTableUUIDColumnSearches = JSON.stringify(itemFinder.scalingTableUUIDColumnSearches);

  return trackInformation;
};

const getItemFinderSearchType = (itemFinder: ItemFinderInputV1): string[] => {
  const types: string[] = [];
  if (itemFinder.propertySearches?.length) types.push(SearchTypeEnum.PROPERTY);
  if (itemFinder.fixedListSearches?.length) types.push(SearchTypeEnum.FIXEDLIST);
  if (itemFinder.hierarchySearches?.length) types.push(SearchTypeEnum.HIERARCHY);
  if (itemFinder.storedTableSearches?.length) types.push(SearchTypeEnum.STOREDTABLE);
  if (itemFinder.scalingTableUUIDColumnSearches?.length) types.push(SearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN);
  return types;
};

export const createUpdateItemFinder = async (
  itemFinder: ItemFinderInputV1,
  itemFinderId?: string,
  showInVantage?: boolean,
): Promise<ItemFinderOutputV1> => {
  const { data: itemFinderOutput } = await (itemFinderId
    ? sqItemsApi.updateItemFinder(itemFinder, { id: itemFinderId })
    : sqItemsApi.createItemFinder(itemFinder));

  if (!showInVantage) {
    const trackAction = itemFinderId ? 'Edit Item Finder' : 'Create Item Finder';
    const trackInformation: TrackInformation = buildItemFinderTrackInformation(itemFinder);
    doTrack('Scaling Table Configuration', trackAction, trackInformation);
  }

  if (!showInVantage && itemFinder.cronSchedule) {
    doTrack('Scaling Table Configuration', 'Set Schedule', {
      numberOfSchedules: itemFinder?.cronSchedule.length,
      schedules: itemFinder?.cronSchedule,
    });
  }

  return itemFinderOutput;
};

export const removeDatasourceProperties = (searchId: string) => {
  const search = sqItemFinderStore.searches.find((search) => search.searchId === searchId);

  if (search) {
    const datasourceIdProperty = search.predicates?.find(
      (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
    );
    const datasourceClassProperty = search.predicates?.find(
      (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceClass,
    );

    if (datasourceClassProperty) {
      updateSearch({
        searchId,
        action: 'UPDATE_PROPERTY',
        propertyId: datasourceClassProperty.propertyId,
        propertyData: {
          datasources: [],
        },
      });
    }

    if (datasourceIdProperty) {
      updateSearch({
        searchId,
        propertyId: datasourceIdProperty.propertyId,
        action: 'REMOVE_PROPERTY',
      });
    }
  }
};

export const loadStoredTableColumns = async (storedTableId: string): Promise<ReactSelectOption[]> => {
  try {
    const { data } = await sqStoredTablesApi.getStoredTable({ id: storedTableId });

    return data.columns.map((col) => ({
      text: col.columnName,
      value: col.id,
    }));
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return [];
  }
};

export const loadHierarchySearchOptions = async (hierarchyId: string): Promise<HierarchySearchOptions> => {
  try {
    const { data: hierarchy } = await sqHierarchiesApi.getHierarchy({ id: hierarchyId });
    const { data: tableDef } = await sqTableDefinitionsApi.getTableDefinition({ id: hierarchy.tableDefinition.id });
    const extraColumns: { text: string; value: string }[] =
      !!hierarchy.aliasColumnId && tableDef.columnDefinitions.some((col) => col.id === hierarchy.aliasColumnId)
        ? [{ text: SeeqNames.Properties.Alias, value: SeeqNames.Properties.Alias }]
        : [];

    const columns = tableDef.columnDefinitions
      .filter(
        (col) =>
          col.columnName !== SeeqNames.MaterializedTables.ItemIdColumn &&
          col.columnName !== SeeqNames.MaterializedTables.DatumIdColumn &&
          col.columnType !== ColumnTypeEnum.UUID,
      )
      .map((col) => ({
        text: col.columnName,
        value: col.columnName,
      }));

    const levelViewColumns = hierarchy.viewColumns
      .filter((col) => col.groupOrder !== undefined)
      .sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0));

    const levelColumnOptions = levelViewColumns.map((viewCol, index) => {
      const colDef = tableDef.columnDefinitions.find((col) => col.id === viewCol.columnDefinition);
      return {
        columnId: viewCol.columnDefinition,
        columnName: colDef?.columnName ?? '',
        columnType: colDef?.columnType ?? '',
        orderValue: index + 1,
      };
    });

    return {
      hierarchyTableProperties: extraColumns.concat(columns),
      hierarchyLevelColumns: levelColumnOptions,
      hierarchy: { id: hierarchy.id, name: hierarchy.name, type: hierarchy.type },
      itemTable: {
        id: tableDef.id,
        name: tableDef.name,
        type: tableDef.type,
        columnId: tableDef.columnDefinitions.find((col) => col.columnName === SeeqNames.MaterializedTables.ItemIdColumn)
          ?.id,
      },
    };
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    throw error;
  }
};

/**
 * Helper function to fetch content types for columns by sampling table data
 */
const getColumnContentTypes = async (
  tableId: string,
  columns: ColumnDefinitionOutputV1[],
): Promise<(ColumnDefinitionOutputV1 & { contentType?: string })[]> => {
  // Build property requests to get Type for each UUID column
  const propertyRequests: PropertiesForItemUUIDColumn[] = columns.map((col) => ({
    uuidColumn: col.columnName,
    propertyNames: [SeeqNames.Properties.Type],
  }));

  const tableData = await getMaterializedTable(
    tableId,
    columns.map((col) => col.columnName),
    propertyRequests,
    undefined,
    1,
  );

  if (!tableData?.rows?.length) {
    return columns.map((col) => ({ ...col, contentType: undefined }));
  }

  return columns.map((col) => {
    const typeHeader = `${col.columnName}.properties.${SeeqNames.Properties.Type}`;
    const typeIndex = tableData.headers.findIndex((h: any) => h.name === typeHeader);
    const sampledType = typeIndex >= 0 ? tableData.rows[0][typeIndex] : undefined;

    return {
      ...col,
      contentType: sampledType as string | undefined,
    };
  });
};

export const loadScalingTableSearchOptions = async (
  tableDefinitionId: string,
  columnDefinitionId?: string,
): Promise<ScalingTableSearchOptions> => {
  try {
    const { data: tableDefinition } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });

    // Build scalingTable display object
    const scalingTable: SelectedSearchItem = {
      id: tableDefinition.id!,
      name: tableDefinition.name!,
      type: SeeqNames.Types.TableDefinition,
    };

    // Build scalingColumn display object if columnDefinitionId exists
    let scalingColumn: SelectedSearchItem | undefined;
    if (columnDefinitionId) {
      const columnDef = tableDefinition.columnDefinitions?.find((col) => col.id === columnDefinitionId);
      if (columnDef) {
        scalingColumn = {
          id: columnDef.id!,
          name: columnDef.columnName!,
          type: SeeqNames.Types.Condition,
          ancestors: [
            {
              id: tableDefinition.id!,
              name: tableDefinition.name!,
              type: SeeqNames.Types.TableDefinition,
            },
          ],
        };
      }
    }

    // Fetch UUID columns with Condition content types
    const uuidColumns =
      tableDefinition.columnDefinitions?.filter((col) => col.columnType === ColumnTypeEnum.UUID) ?? [];

    if (uuidColumns.length === 0) {
      return { scalingTable, scalingColumn, scalingTableColumns: [] };
    }

    const columnsWithTypes = await getColumnContentTypes(tableDefinition.id!, uuidColumns);
    const scalingTableColumns: ColumnDefinition[] = columnsWithTypes
      .filter(
        (col) =>
          col.contentType &&
          [SeeqNames.Types.Condition, SeeqNames.Types.StoredCondition, SeeqNames.Types.CalculatedCondition].includes(
            col.contentType as any,
          ),
      )
      .map((col) => ({
        ...col,
        displayName: col.columnName || '',
      })) as ColumnDefinition[];

    return { scalingTable, scalingColumn, scalingTableColumns };
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    throw error;
  }
};

/**
 * Converts a list of HierarchyPaths into SelectedSearchItems with proper ancestor chains and IDs.
 *
 * @param hierarchyPaths - The list of hierarchy paths to convert.
 * @param hierarchyLevelColumns - The level column definitions for the hierarchy.
 * @param searchItemTable - The item table selected for this search, used to determine UUID columns.
 * @returns An array of SelectedSearchItems representing the selected nodes.
 */
export const getHierarchySelectedNodes = (
  hierarchyPaths: HierarchyPath[],
  hierarchyLevelColumns: HierarchyLevelColumn[],
  searchItemTable: SelectedSearchItem | undefined,
): SelectedSearchItem[] => {
  function buildUniqueHierarchyNodeId(path: HierarchyPath): string {
    return path.nodes.reduce((generatedId, node) => {
      const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId)!;

      if ((node && pathColumn.columnType === ColumnTypeEnum.UUID) || node.columnId === searchItemTable?.columnId) {
        return node.value!;
      } else if (node && pathColumn.columnType !== ColumnTypeEnum.UUID) {
        return `${generatedId}|${node.value}-${pathColumn?.columnId}`;
      }
      return generatedId;
    }, '');
  }

  return hierarchyPaths.map((path) => {
    const finalNode = path.nodes[path.nodes.length - 1]!;
    const finalPathColumn = hierarchyLevelColumns.find((col) => col.columnId === finalNode.columnId);

    return {
      id: finalPathColumn?.columnType === ColumnTypeEnum.UUID ? finalNode.value! : buildUniqueHierarchyNodeId(path),
      columnId: finalNode.columnId,
      columnValue: finalNode.value,
      ancestors: path.nodes.slice(0, -1).map((node, nodeIndex) => {
        const pathColumn = hierarchyLevelColumns.find((col) => col.columnId === node.columnId);

        return {
          id:
            pathColumn?.columnType === ColumnTypeEnum.UUID
              ? node.value!
              : buildUniqueHierarchyNodeId({ nodes: path.nodes.slice(0, nodeIndex + 1) }),
          name: '',
          type: '',
          columnId: node.columnId,
          columnValue: node.value,
        };
      }),
    };
  });
};

export const addDatasourcePropertyAndValue = (
  searchId: string,
  propertyId: string,
  valueId: string,
  selectedDatasources: SelectOptionIF<Datasource>[],
  ancestorPredicateId?: string,
) => {
  const datasourceLabel: DatasourceLabel[] = [];
  const selectedDatasourceIds = selectedDatasources.map((datasource) => datasource.value.datasourceId as string);
  const datasourceClassValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];
  const datasourceIdValues: { value: string; propertyId: string; searchId: string; valueId: string }[] = [];

  const search = sqItemFinderStore.searches.find((search) => search.searchId === searchId);
  const datasourceIdProperty = search?.predicates?.find(
    (predicate) => predicate.propertyName === SeeqNames.Properties.DatasourceId,
  );

  selectedDatasources.forEach((datasource) => {
    const { datasourceClass, datasourceId, name, id } = datasource.value;

    datasourceClassValues.push({
      value: datasourceClass as string,
      propertyId,
      searchId,
      valueId: base64guid(),
    });
    if (datasourceIdProperty) {
      datasourceIdValues.push({
        value: datasourceId as string,
        propertyId: datasourceIdProperty?.propertyId,
        searchId,
        valueId: base64guid(),
      });
    }
    datasourceLabel.push({
      label: datasource.value.name,
      value: {
        datasourceClass: datasourceClass as string,
        datasourceId: datasourceId as string,
        name,
        id: id as string,
      },
    });
  });

  // Update the property to contain the datasource id and name for the first selected datasource
  updateSearch({
    searchId,
    action: 'UPDATE_PROPERTY',
    propertyId,
    propertyData: {
      datasources: !selectedDatasources.length ? [] : datasourceLabel,
      values: !selectedDatasources.length ? [{ value: '', propertyId, searchId, valueId }] : datasourceClassValues,
      columnId: undefined,
      isColumnSelect: false,
    },
    ancestorPredicateId,
  });

  // If there is no datasourceId property, add it. Otherwise, update the values
  if (!datasourceIdProperty) {
    updateSearch({
      searchId,
      action: 'ADD_PROPERTY',
      propertyData: {
        propertyName: SeeqNames.Properties.DatasourceId,
        operator: OperatorEnum.WITHIN,
        isColumnSelect: false,
      },
      propertyValues: selectedDatasourceIds,
      ancestorPredicateId,
    });
  } else {
    updateSearch({
      searchId,
      action: 'UPDATE_PROPERTY',
      propertyId: datasourceIdProperty.propertyId,
      propertyData: {
        values: !selectedDatasources.length
          ? [
              {
                value: '',
                propertyId: datasourceIdProperty.propertyId,
                searchId,
                valueId: datasourceIdProperty?.values[0]?.valueId,
              },
            ]
          : datasourceIdValues,
        columnId: undefined,
        isColumnSelect: false,
      },
      ancestorPredicateId,
    });
  }
};

export async function findDatasourceNamesAndIds(datasourceClass: string) {
  try {
    const { data } = await sqDatasourcesApi.getDatasources({ datasourceClass });
    if (data.datasources) {
      return {
        name: data.datasources[0].name,
        id: data.datasources[0].id,
        datasourceId: data.datasources[0].datasourceId,
      };
    }
  } catch (error) {
    errorToast({ httpResponseOrError: error });
  }
}

export const getItemFinder = async (itemFinderId: string): Promise<ItemFinderOutputV1 | undefined> => {
  try {
    const { data: itemFinderOutput } = await sqItemsApi.getItemFinder({ id: itemFinderId });
    return itemFinderOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const addEmptyPropertyToSearch = (search: Search) => {
  if (
    search.searchType === SearchTypeEnum.PROPERTY ||
    search.searchType === SearchTypeEnum.STOREDTABLE ||
    ((search.searchType as SearchType) === SearchTypeEnum.WORKBENCH && !search.predicates)
  ) {
    updateSearch({
      searchId: search.searchId,
      action: 'ADD_PROPERTY',
    });
  }
};

export const isValidAIPayload = (payload: Record<string, unknown>): payload is AIPayload => {
  const { propertySearches, fixedListSearches } = payload;
  if (!propertySearches && !fixedListSearches) {
    return false;
  }
  if (propertySearches && Array.isArray(propertySearches)) {
    if (!propertySearches.every(isPropertySearch)) {
      return false;
    }
  }
  if (fixedListSearches && Array.isArray(fixedListSearches)) {
    if (!fixedListSearches.every(isFixedListSearch)) {
      return false;
    }
  }
  return true;
};

export const isHierarchySearch = (search: unknown): search is HierarchySearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchV1).searchType === SearchTypeEnum.HIERARCHY;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'hierarchyId' in search;
};

export const isHierarchySearchOutput = (search: unknown): search is HierarchySearchOutputV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as HierarchySearchOutputV1).searchType === SearchTypeEnum.HIERARCHY;
  return (
    searchTypeCorrect &&
    typeof search === 'object' &&
    !!search &&
    'hierarchy' in search &&
    search.hierarchy !== undefined
  );
};

export const isStoredTableSearch = (search: unknown): search is StoredTableSearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as StoredTableSearchV1).searchType === SearchTypeEnum.STOREDTABLE;
  return searchTypeCorrect && typeof search === 'object' && !!search && 'storedTableId' in search;
};

export const isScalingTableSearch = (search: unknown): search is ScalingTableSearch => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect =
    (search as ScalingTableUUIDColumnSearchV1).searchType === SearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN;
  return searchTypeCorrect && 'tableDefinitionId' in search;
};

/**
 * Format scaling table searches for API submission
 * Filters to only valid searches (with both table and column) and maps to API format
 */
export const scalingTableSearchToColumnSearchOutput = (
  searches: ScalingTableSearch[],
): ScalingTableUUIDColumnSearchV1[] => {
  return searches
    .filter((search) => search.tableDefinitionId && search.columnDefinitionId)
    .map((search) => ({
      searchType: ScalingTableSearchTypeEnum.MATERIALIZEDTABLEUUIDCOLUMN,
      isInclude: search.isInclude,
      tableDefinitionId: search.tableDefinitionId!,
      columnDefinitionId: search.columnDefinitionId!,
      types: ['Condition', 'StoredCondition', 'CalculatedCondition'],
      rowIds: [],
      searchOrder: search.searchOrder,
    }));
};

const isFixedListSearch = (search: unknown): search is FixedListSearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  const searchTypeCorrect = (search as models.FixedListSearchV1).searchType === SearchTypeEnum.FIXEDLIST;
  const isIncludeCorrect = typeof (search as models.FixedListSearchV1).isInclude === 'boolean';
  const itemIdsCorrect = Array.isArray((search as models.FixedListSearchV1).itemIds);
  return searchTypeCorrect && isIncludeCorrect && itemIdsCorrect;
};

const isPropertySearch = (search: unknown): search is PropertySearchV1 => {
  if (!search || typeof search !== 'object') {
    return false;
  }
  if ((search as PropertySearchV1).searchType !== SearchTypeEnum.PROPERTY) {
    return false;
  }
  if (!('searchType' in search) || !('isInclude' in search) || !('types' in search)) {
    return false;
  }

  if ('predicates' in search) {
    if (!Array.isArray((search as PropertySearchV1).predicates)) {
      return false;
    }
    if (!(search as PropertySearchV1).predicates?.every(isPropertyFilterInputV1)) {
      return false;
    }
  }
  return true;
};

const isPropertyFilterInputV1 = (predicate: unknown): predicate is PropertyFilterInputV1 => {
  if (!predicate || typeof predicate !== 'object') {
    return false;
  }
  if (!('operator' in predicate) || !('propertyName' in predicate) || !('values' in predicate)) {
    return false;
  }
  if (!Array.isArray((predicate as PropertyFilterInputV1).values)) {
    return false;
  }
  return (predicate as PropertyFilterInputV1).values.every(isPropertyInputV1);
};

const isPropertyInputV1 = (value: unknown): value is PropertyInputV1 => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'value' in value;
};
